import { useState } from 'react'
import { Body1, Caption1, Caption2, Heading6, Title2 } from '@/components/ui/typography'
import { TrashIcon } from 'icons/TrashIcon'
import { BarChart2 } from 'lucide-react'

import { DashboardLayout } from '../types'

interface LayoutSelectorProps {
  layouts: DashboardLayout[]
  currentLayoutId: string
  onLayoutSelect: (layoutId: string) => void
  onCreateNew: () => void
  onDeleteLayout: (layoutId: string) => void
  canAddMore: boolean
}

export const LayoutSelector: React.FC<LayoutSelectorProps> = ({
  layouts,
  currentLayoutId,
  onLayoutSelect,
  onCreateNew,
  onDeleteLayout,
  canAddMore,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const currentLayout = layouts.find((l) => l.id === currentLayoutId)

  const handleSelect = (layoutId: string) => {
    onLayoutSelect(layoutId)
    setIsOpen(false)
  }

  const handleDelete = (e: React.MouseEvent, layoutId: string) => {
    console.log({ layoutId })
    e.stopPropagation()
    onDeleteLayout(layoutId)
  }

  return (
    <div className='relative'>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='
        flex min-w-[18rem] items-center gap-2
        rounded-2xl border border-[var(--border)] px-4 py-2.5
        text-sm font-medium transition-all
        hover:border-[var(--sidebar-primary)] hover:shadow-sm
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ring)]
      '
      >
        <BarChart2 className='h-4 w-4' />
        <Body1 className='text-sm'>{currentLayout?.name || 'Select Layout'}</Body1>
        <span className='ml-auto text-xs opacity-60'>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <>
          <div className='fixed inset-0 z-[999]' onClick={() => setIsOpen(false)} />

          <div
            className='
            absolute right-0 top-[calc(100%+0.5rem)]
            z-[1000]
            max-h-[25rem] min-w-[18rem]
            overflow-y-auto
            rounded-lg border border-[var(--border)]
            bg-white shadow-lg
          '
          >
            <div className='p-2 border-b border-black'>
              <Title2
                uppercase
                className='px-2 py-1 text-[14px] tracking-wider text-[var(--muted-foreground)] leading-none '
              >
                Preset layouts
              </Title2>

              {layouts
                .filter((l) => l.isPreset)
                .map((layout) => {
                  const isActive = layout.id === currentLayoutId

                  return (
                    <div
                      key={layout.id}
                      onClick={() => handleSelect(layout.id)}
                      className={`
                      flex cursor-pointer items-center justify-between rounded-md p-3 transition-colors
                      hover:bg-[var(--accent)]
                      ${isActive ? 'bg-[var(--accent)]' : ''}
                    `}
                    >
                      <div className='flex flex-1 flex-col gap-1'>
                        <Body1
                          semibold
                          className={`${isActive ? 'text-[var(--sidebar-primary)] font-semibold' : 'text-primary'}`}
                        >
                          {layout.name}
                        </Body1>

                        <Caption1 className='leading-none text-gray-500'>
                          {layout.description}
                        </Caption1>

                        <Caption2 className='text-[10px] text-muted-foreground'>
                          {layout.description}
                        </Caption2>
                        <Caption2 className='text-[10px] text-gray-500'>
                          {layout.widgets.length} widgets
                        </Caption2>
                      </div>

                      {isActive && (
                        <Heading6 bold className='text-[var(--sidebar-primary)] text-base'>
                          ✓
                        </Heading6>
                      )}
                    </div>
                  )
                })}
            </div>

            {layouts.some((l) => !l.isPreset) && (
              <div className='p-2 border-b border-black'>
                <Title2
                  semibold
                  uppercase
                  className=' text-[14px] px-2 py-1 tracking-wider text-[var(--muted-foreground)]'
                >
                  Custom layouts
                </Title2>

                {layouts
                  .filter((l) => !l.isPreset)
                  .map((layout) => {
                    const isActive = layout.id === currentLayoutId

                    return (
                      <div
                        key={layout.id}
                        onClick={() => handleSelect(layout.id)}
                        className={`
                        flex cursor-pointer items-center justify-between rounded-md p-3 max-w-[18rem] transition-colors
                        hover:bg-[var(--accent)]
                        ${isActive ? 'bg-[var(--accent)]' : ''}
                      `}
                      >
                        <div className='flex flex-1 flex-col gap-1'>
                          <Body1
                            font-semibold
                            className={`${isActive ? 'text-[var(--sidebar-primary)] font-semibold' : 'text-primary'}`}
                          >
                            {layout.name}
                          </Body1>

                          <Caption2 className='text-[10px] text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap'>
                            {layout.description}
                          </Caption2>

                          <Caption2 className='leading-none text-gray-500'>
                            {layout.widgets.length} widgets
                          </Caption2>
                        </div>

                        <div className='flex items-center gap-2'>
                          {isActive && (
                            <Caption2 font-bold className='text-[var(--sidebar-primary)] text-base'>
                              ✓
                            </Caption2>
                          )}

                          <button
                            onClick={(e) => handleDelete(e, layout.id)}
                            className='
                            rounded-md p-1 transition-all
                            hover:scale-105 hover:text-red-500
                          '
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}

            {canAddMore && (
              <div className='border-t border-[var(--border)] p-3'>
                <button
                  onClick={() => {
                    setIsOpen(false)
                    onCreateNew()
                  }}
                  className='w-full rounded-lg bg-[var(--sidebar-primary)] px-4 py-2.5 text-sm font-medium text-white transition-all hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ring)]'
                >
                  + Create New Layout
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
